/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { Global, jsx, css } from '@emotion/react';

import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';

const aiSuggestionsInlineCommentResetStyles = css({
	padding: 'inherit',
	borderBottom: 'inherit',
	boxShadow: 'inherit',
});

const aiSuggestionsSelectedInlineCommentBackgroundStyles = css({
	backgroundColor: token('color.background.accent.yellow.subtlest', '#FFF7D6'),
});

const spellingErrorStyles = css({
	borderBottom: `2px solid ${token('color.background.accent.magenta.subtle.hovered', '#F797D2')}`,
	borderBottomStyle: 'dashed',
});

const autoCorrectStyles = css({
	borderBottom: `2px solid ${token('color.text.accent.teal', '#206A83')}`,
	borderBottomStyle: 'dotted',
});

const spellingAndGrammarSuggestionStyles = css({
	borderBottom: `2px dotted ${token('color.chart.blue.bolder', '#1D7AFC')}`,
	cursor: 'pointer',
});

const spellingAndGrammarSuggestionSelectedStyles = css({
	borderBottom: `2px dotted ${token('color.chart.blue.bolder', '#1D7AFC')}`,
	backgroundColor: token('color.background.accent.blue.subtlest', '#E9F2FF'),
	cursor: 'pointer',
});

const proactiveRecommendationStyles = css({
	borderBottom: `2px dotted ${token('color.chart.blue.bolder', '#1D7AFC')}`,
	cursor: 'pointer',
});

const proactiveRecommendationSelectedStyles = css({
	borderBottom: `2px dotted ${token('color.chart.blue.bolder', '#1D7AFC')}`,
	backgroundColor: token('color.background.accent.blue.subtlest', '#E9F2FF'),
	cursor: 'pointer',
});

// Reduce ai improve writing button padding as per designs
const aiSelectionToolbarButtonStyles = () => {
	if (editorExperiment('platform_editor_controls', 'variant1')) {
		// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
		return css`
			/* Reduce ai button padding as per designs */
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'],
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:disabled,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:hover,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:focus {
				padding: 0px ${token('space.075', '6px')};
			}

			/* Reduce annotation button padding as per designs */
			button[data-testid='ak-editor-annotation-toolbar-create-button'],
			button[data-testid='ak-editor-annotation-toolbar-create-button']:disabled,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:hover,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:focus {
				padding: 0px ${token('space.075', '6px')};
				span:first-child {
					margin: 0;
				}
				span[role='img'],
				svg {
					padding: 0;
				}
			}
		`;
	} else {
		// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
		return css`
			/* Reduce ai button padding as per designs */
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'],
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:disabled,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:hover,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:focus,
			/* Reduce annotation button padding as per designs */
			button[data-testid='ak-editor-annotation-toolbar-create-button'],
			button[data-testid='ak-editor-annotation-toolbar-create-button']:disabled,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:hover,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:focus {
				padding: 0px ${token('space.100', '8px')};
			}

			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'] svg {
				padding-right: ${token('space.050', '4px')};
			}

			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'] > span:only-child {
				svg {
					padding: 0px ${token('space.100', '8px')};
				}
			}
		`;
	}
};

export const globalStyles = () => {
	// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
	return css`
		.akEditor div.ProseMirror {
			/**
			 * Proactive styles
			 */

			.ai-proactive-recommendation {
				${proactiveRecommendationStyles}
				.ak-editor-annotation-draft,
        		.ak-editor-annotation-blur,
        		.ak-editor-annotation-focus {
					border-bottom: inherit;
					box-shadow: inherit;
				}
			}

			.ai-proactive-recommendation-selected,
			.ai-proactive-recommendation-hovered {
				${proactiveRecommendationSelectedStyles}
				.ak-editor-annotation-draft,
        		.ak-editor-annotation-blur,
       			 .ak-editor-annotation-focus {
					border-bottom: inherit;
					box-shadow: inherit;
					background-color: inherit;
				}
			}

			/**
			 * Spelling grammar styles
			 */

			.ai-spelling-error {
				${spellingErrorStyles}
			}

			.ai-auto-correct {
				${autoCorrectStyles}
			}

			.ai-suggestions {
				${spellingAndGrammarSuggestionStyles}
			}

			.ai-suggestions-selected {
				${spellingAndGrammarSuggestionSelectedStyles}
			}

			/* style for inline comment */
			.ai-suggestions {
				.ak-editor-annotation-draft,
				.ak-editor-annotation-blur,
				.ak-editor-annotation-focus {
					${aiSuggestionsInlineCommentResetStyles}
				}
			}

			/* style for inline comment is clicked */
			.ai-suggestions-selected {
				&.ak-editor-annotation-blur,
				&.ak-editor-annotation-focus {
					${aiSuggestionsSelectedInlineCommentBackgroundStyles}
				}
			}

			/* style for inline comment is clicked and then focus on somewhere outside the editor */
			/* (clicking outside the editor or opening the devtools) */
			.ak-editor-annotation-focus {
				&:has(.ai-suggestions-selected) {
					${aiSuggestionsInlineCommentResetStyles}
				}

				.ai-suggestions-selected {
					${aiSuggestionsSelectedInlineCommentBackgroundStyles}
				}
			}
		}

		${aiSelectionToolbarButtonStyles()}
	`;
};

export const GlobalWrapper = ({ children }: { children: React.ReactNode }) => {
	return (
		<>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
			<Global styles={globalStyles()} />
			{children}
		</>
	);
};
